import type { User } from '../../queries/teamQuery';
import type { ZustandStore } from '../store.type';
import type { NamedSet } from 'zustand/middleware';

export const cardNameText = 'name';

export type CardFormData = {
	id?: string;
	address: string;
	cardNumber: string;
	city: string;
	countryCode: string;
	email: string;
	expirationMonth: string;
	expirationYear: string;
	firstName: string;
	lastName: string;
	name: string;
	orgName: string;
	regionCode?: string | undefined;
	userId: string;
	zipCode: string;
	billingCurrency: string;
};

type CardSourceState = {
	cardSource: CardFormData;
	cardSourceErrors: Partial<Record<keyof CardFormData, string>> | null;
};

type CardSourceActions = {
	cardSourceReset: (user?: Partial<User>) => void;
	cardSourceSetAddress: (address: CardSourceState['cardSource']['address']) => void;
	cardSourceSetCardNumber: (cardNumber: CardSourceState['cardSource']['cardNumber']) => void;
	cardSourceSetCity: (city: CardSourceState['cardSource']['city']) => void;
	cardSourceSetCountryCode: (countryCode: CardSourceState['cardSource']['countryCode']) => void;
	cardSourceSetEmail: (email: CardSourceState['cardSource']['email']) => void;
	cardSourceSetExpirationMonth: (expirationMonth: CardSourceState['cardSource']['expirationMonth']) => void;
	cardSourceSetExpirationYear: (expirationYear: CardSourceState['cardSource']['expirationYear']) => void;
	cardSourceSetFirstName: (firstName: CardSourceState['cardSource']['firstName']) => void;
	cardSourceSetLastName: (lastName: CardSourceState['cardSource']['lastName']) => void;
	cardSourceSetName: (name: CardSourceState['cardSource']['name']) => void;
	cardSourceSetOrgName: (orgName: CardSourceState['cardSource']['orgName']) => void;
	cardSourceSetRegionCode: (regionCode: CardSourceState['cardSource']['regionCode']) => void;
	cardSourceSetUserId: (userId: CardSourceState['cardSource']['userId']) => void;
	cardSourceSetZipCode: (zipCode: CardSourceState['cardSource']['zipCode']) => void;
	cardSourceSetErrors: (cardSourceErrors: CardSourceState['cardSourceErrors']) => void;
};

export type CardSourceSlice = CardSourceState & CardSourceActions;

const getCardSource = (user?: Partial<User>) => ({
	address: '',
	cardNumber: '',
	city: '',
	countryCode: 'US',
	email: user?.email || '',
	expirationMonth: '',
	expirationYear: '',
	firstName: user?.firstName || '',
	lastName: user?.lastName || '',
	name: '',
	orgName: user?.companyName || '',
	regionCode: '',
	userId: user?.userId || '',
	zipCode: '',
	billingCurrency: 'USD',
});

const cardSourceInitialState: CardSourceState = {
	cardSource: getCardSource(),
	cardSourceErrors: null,
};

export function cardSourceSlice(set: NamedSet<ZustandStore>): CardSourceSlice {
	return {
		...cardSourceInitialState,

		cardSourceReset(user?: Partial<User>): void {
			set(
				state => {
					state.cardSource = getCardSource(user);
					return state;
				},
				false,
				'[cardSourceReset]',
			);
		},

		cardSourceSetAddress(address: CardSourceState['cardSource']['address']): void {
			set(
				state => {
					state.cardSource.address = address;
					return state;
				},
				false,
				'[setAddress]',
			);
		},
		cardSourceSetCardNumber(cardNumber: CardSourceState['cardSource']['cardNumber']): void {
			set(
				state => {
					state.cardSource.cardNumber = cardNumber;
					return state;
				},
				false,
				'[setCardNumber]',
			);
		},
		cardSourceSetCity(city: CardSourceState['cardSource']['city']): void {
			set(
				state => {
					state.cardSource.city = city;
					return state;
				},
				false,
				'[setCity]',
			);
		},
		cardSourceSetCountryCode(countryCode: CardSourceState['cardSource']['countryCode']): void {
			set(
				state => {
					state.cardSource.countryCode = countryCode;
					return state;
				},
				false,
				'[setCountryCode]',
			);
		},
		cardSourceSetEmail(email: CardSourceState['cardSource']['email']): void {
			set(
				state => {
					state.cardSource.email = email;
					return state;
				},
				false,
				'[setEmail]',
			);
		},
		cardSourceSetExpirationMonth(expirationMonth: CardSourceState['cardSource']['expirationMonth']): void {
			set(
				state => {
					state.cardSource.expirationMonth = expirationMonth;
					return state;
				},
				false,
				'[setExpirationMonth]',
			);
		},
		cardSourceSetExpirationYear(expirationYear: CardSourceState['cardSource']['expirationYear']): void {
			set(
				state => {
					state.cardSource.expirationYear = expirationYear;
					return state;
				},
				false,
				'[setExpirationYear]',
			);
		},
		cardSourceSetFirstName(firstName: CardSourceState['cardSource']['firstName']): void {
			set(
				state => {
					state.cardSource.firstName = firstName;
					return state;
				},
				false,
				'[setFirstName]',
			);
		},
		cardSourceSetLastName(lastName: CardSourceState['cardSource']['lastName']): void {
			set(
				state => {
					state.cardSource.lastName = lastName;
					return state;
				},
				false,
				'[setLastName]',
			);
		},
		cardSourceSetName(name: CardSourceState['cardSource']['name']): void {
			set(
				state => {
					state.cardSource.name = name;
					return state;
				},
				false,
				'[setName]',
			);
		},
		cardSourceSetOrgName(orgName: CardSourceState['cardSource']['orgName']): void {
			set(
				state => {
					state.cardSource.orgName = orgName;
					return state;
				},
				false,
				'[setOrgName]',
			);
		},
		cardSourceSetRegionCode(regionCode: CardSourceState['cardSource']['regionCode']): void {
			set(
				state => {
					state.cardSource.regionCode = regionCode;
					return state;
				},
				false,
				'[setRegionCode]',
			);
		},
		cardSourceSetUserId(userId: CardSourceState['cardSource']['userId']): void {
			set(
				state => {
					state.cardSource.userId = userId;
					return state;
				},
				false,
				'[setUserId]',
			);
		},
		cardSourceSetZipCode(zipCode: CardSourceState['cardSource']['zipCode']): void {
			set(
				state => {
					state.cardSource.zipCode = zipCode;
					return state;
				},
				false,
				'[setZipCode]',
			);
		},
		cardSourceSetErrors(cardSourceErrors: CardSourceState['cardSourceErrors']): void {
			set(
				state => {
					state.cardSourceErrors = cardSourceErrors;
					return state;
				},
				false,
				'[cardSourceSetErrors]',
			);
		},
	};
}
